import { useCallback } from "react";
import { useToken } from "../../../server/auth/use-token";
import { ImagesService } from "../../../server/cloudflare/images-service";
import { MediaService } from "../../../server/cloudflare/media-service";
import { VideoUidService } from "../../assets/client/video-uid-service";
import { useGetDesignHuddleAccessToken } from "../../design-huddle/use-design-huddle-access-token";
import { copyImage, getDesignHuddleProjectRenderFromPageId } from "../../design-huddle/utils";

import type { UUID } from "../../../utils/uuid";
import type { AssetResult } from "../../assets/hooks/use-upsert-asset";
import type { SaveAssetHandler } from "../types";

export async function getRenderUrlImageEngine({
  getToken,
  accessToken,
  projectId,
  format,
  filename,
  onProgress,
}: {
  getToken: () => Promise<string | null>;
  accessToken: string;
  projectId: string;
  pageId?: string;
  format: string;
  filename: string;
  onProgress?: (progress: number | null) => void;
}): Promise<{ success: true; url: string } | { success: false }> {
  const url = await getDesignHuddleProjectRenderFromPageId({
    getToken,
    accessToken,
    projectId,
    format,
    filename,
    onProgress,
  });

  return await copyImage(getToken, url, `asset:thumbnail:${projectId}:${Date.now()}`);
}

export async function getRenderUrlCloudflareStream({
  getToken,
  accessToken,
  projectId,
  format,
  filename,
  id,
  onProgress,
}: {
  getToken: () => Promise<string>;
  accessToken: string;
  projectId: string;
  pageId?: string;
  format: string;
  filename: string;
  id?: UUID;
  onProgress?: (progress: number | null) => void;
}): Promise<{ success: true; uid: string; url: string } | { success: false }> {
  const url = await getDesignHuddleProjectRenderFromPageId({
    getToken,
    accessToken,
    projectId,
    format,
    filename,
    onProgress: onProgress && ((progress) => progress && onProgress(progress < 60 ? progress : 60)),
  });
  onProgress?.(70);

  const generateURLResponse = await new ImagesService().generateDirectUploadURL(await getToken());
  if (!generateURLResponse.success) {
    return { success: false };
  }

  // Fetch the video from the Design Huddle render URL
  const videoResponse = await fetch(url);
  if (!videoResponse.ok) {
    return { success: false };
  }

  const videoBlob = await videoResponse.blob();
  const videoFile = new File([videoBlob], `${filename}.${format}`, {
    type: `video/${format}`,
  });
  const formData = new FormData();
  formData.append("file", videoFile);

  const uploadVideoResponse = await fetch(generateURLResponse.data.uploadURL, {
    method: "POST",
    body: formData,
  });

  if (!uploadVideoResponse.ok) {
    return { success: false };
  }

  onProgress?.(90);
  const videoUidService = new VideoUidService(await getToken());
  const { data, error } = await videoUidService.upsert(generateURLResponse.data.uid, id);
  if (error || !data) {
    return { success: false };
  }

  const mediaService = new MediaService();

  onProgress?.(100);
  return {
    success: true,
    uid: generateURLResponse.data.uid,
    url: mediaService.constructLandingPageURL(data.id),
  };
}

export const useSaveAssetCallback = ({
  onSave,
  title,
  projectId,
  multipage,
  contentType,
  id,
  onProgress,
}: {
  title: string;
  onSave?: SaveAssetHandler;
  projectId: string;
  multipage: boolean;
  contentType: string;
  onProgress?: (progress: number | null) => void;
  id?: UUID;
}): (() => Promise<AssetResult | undefined>) => {
  const getToken = useToken();
  const getAccessToken = useGetDesignHuddleAccessToken();

  return useCallback(
    () =>
      (async () => {
        onProgress?.(10);
        const publicFormat = contentType === "Video" ? "mp4" : multipage ? "pdf" : undefined;

        const videoUrlRequest =
          publicFormat === "mp4"
            ? getRenderUrlCloudflareStream({
                getToken,
                accessToken: await getAccessToken([projectId]),
                projectId,
                format: publicFormat,
                filename: projectId,
                id,
                onProgress,
              })
            : undefined;

        const pdfUrlRequest =
          publicFormat === "pdf"
            ? getRenderUrlImageEngine({
                getToken,
                accessToken: await getAccessToken([projectId]),
                projectId,
                format: publicFormat,
                filename: projectId,
                onProgress,
              })
            : undefined;

        const [png, videoRender, pdfRender] = await Promise.all([
          getRenderUrlImageEngine({
            getToken,
            accessToken: await getAccessToken([projectId]),
            projectId,
            format: "png",
            filename: projectId,
            onProgress: !publicFormat ? onProgress : undefined,
          }),
          videoUrlRequest,
          pdfUrlRequest,
        ]);

        if (
          !png.success ||
          (videoRender && !videoRender.success) ||
          (pdfRender && !pdfRender.success)
        ) {
          return await onSave?.({ title, projectId });
        }

        if (videoRender?.success) {
          return await onSave?.({
            title,
            thumbnailUrl: new ImagesService().getDHThumbnailUrl(png.url)!,
            publicUrl: videoRender.url,
            projectId,
            externalStorageId: videoRender.uid,
          });
        }

        if (pdfRender?.success) {
          return await onSave?.({
            title,
            thumbnailUrl: png.url,
            publicUrl: pdfRender.url,
            projectId,
          });
        }

        if (png.success) {
          return await onSave?.({
            title,
            thumbnailUrl: png.url,
            publicUrl: png.url,
            projectId,
          });
        }
      })(),
    [contentType, multipage, getToken, getAccessToken, projectId, onSave, title, id, onProgress],
  );
};
